"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Mail, Phone, Sparkles, TrendingUp, ShieldCheck, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const guarantees = [{
  title: "Direct Consultant Access",
  desc: "You work with me directly on every audit, strategy, and execution step."
}, {
  title: "Tailored SEO Strategy",
  desc: "Custom search roadmaps designed specifically for your target audience, industry, and revenue goals."
}, {
  title: "Transparent Reporting",
  desc: "Clear deliverables, measurable organic growth, and honest communication without vanity metrics."
}];
const highlights = [{
  label: "Strategy Format",
  value: "Custom Action Plan"
}, {
  label: "Turnaround Time",
  value: "Quick Strategy Roadmaps"
}, {
  label: "Specialisms",
  value: "Technical, B2B & AI Search"
}];
export default function Calltoaction() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="calltoaction" data-nav="light" className="relative overflow-hidden bg-background py-20 lg:py-28">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-radial from-secondary/40 via-transparent to-transparent opacity-70" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <Card className="relative overflow-hidden border border-border/80 bg-card/60 p-2 shadow-xl backdrop-blur-xs sm:p-4 md:p-8">
            <div className="rounded-xl border border-border/60 bg-background p-6 sm:p-10 lg:p-12">
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
                
                <div className="lg:col-span-7">
                  <motion.div initial={shouldReduceMotion ? {
                  opacity: 1
                } : {
                  opacity: 0,
                  y: 16
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  ease: "easeOut"
                }} className="space-y-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary" className="gap-1.5 px-3 py-1 font-medium text-foreground">
                        <Sparkles className="size-3.5 text-accent" />
                        SEO Consultation
                      </Badge>
                      <Badge variant="outline" className="gap-1.5 px-3 py-1 text-muted-foreground">
                        <ShieldCheck className="size-3.5 text-primary" />
                        Sustainable Growth
                      </Badge>
                    </div>

                    <div className="space-y-3">
                      <h2 className="font-default text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                        Ready to Grow Your Organic Search Visibility?
                      </h2>
                      <p className="font-default text-base leading-relaxed text-muted-foreground sm:text-lg">
                        Let us examine your search performance, uncover high-intent revenue opportunities, and build an actionable roadmap tailored for your business.
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center">
                      <Button asChild size="lg" className="gap-2 shadow-md">
                        <Link to="/ContactPage">
                          <span>Book a Free SEO Consultation</span>
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" size="lg" className="gap-2 border-border/80">
                        <Link to="/CaseStudiesPage">
                          <TrendingUp className="size-4 text-primary" />
                          <span>Explore Case Studies</span>
                        </Link>
                      </Button>
                    </div>

                    <div className="pt-2">
                      <p className="font-default text-xs text-muted-foreground">
                        {"Prefer a direct message? Reach me at "}
                        <a href="mailto:contact@ye-consultancy.com" className="font-medium text-foreground underline underline-offset-4 transition-colors hover:text-primary">
                          contact@ye-consultancy.com
                        </a>
                        {" or call "}
                        <a href="tel:+447490984430" className="font-medium text-foreground underline underline-offset-4 transition-colors hover:text-primary">
                          +44 7490984430
                        </a>
                      </p>
                    </div>
                  </motion.div>
                </div>

                <div className="lg:col-span-5">
                  <motion.div initial={shouldReduceMotion ? {
                  opacity: 1
                } : {
                  opacity: 0,
                  y: 16
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.15,
                  ease: "easeOut"
                }}>
                    <div className="rounded-xl border border-border bg-muted/40 p-6 sm:p-7">
                      <div className="mb-5 flex items-center gap-3">
                        <div className="flex size-10 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
                          <UserCheck className="size-5" />
                        </div>
                        <div>
                          <p className="font-default text-sm font-semibold text-foreground">
                            Working With Yassine Essaadi
                          </p>
                          <p className="font-default text-xs text-muted-foreground">
                            Independent SEO Strategy & Execution
                          </p>
                        </div>
                      </div>

                      <Separator className="mb-5 bg-border/80" />

                      <div className="space-y-4">
                        {guarantees.map((item, i) => <div key={item.title} data-index={i} className="flex items-start gap-3">
                            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                            <div className="space-y-0.5">
                              <p className="font-default text-sm font-semibold text-foreground">
                                {item.title}
                              </p>
                              <p className="font-default text-xs leading-relaxed text-muted-foreground">
                                {item.desc}
                              </p>
                            </div>
                          </div>)}
                      </div>

                      <Separator className="my-5 bg-border/80" />

                      <div className="grid grid-cols-1 gap-2 pt-1 text-xs text-muted-foreground">
                        <div className="flex items-center justify-between font-medium">
                          <span className="flex items-center gap-1.5">
                            <Mail className="size-3.5 text-muted-foreground" />
                            Email Inquiries:
                          </span>
                          <span className="text-foreground">24-hour response</span>
                        </div>
                        <div className="flex items-center justify-between font-medium">
                          <span className="flex items-center gap-1.5">
                            <Phone className="size-3.5 text-muted-foreground" />
                            Initial Consultation:
                          </span>
                          <span className="text-foreground">100% Free & No Obligation</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>

              </div>

              <div className="mt-10 border-t border-border/80 pt-8">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  {highlights.map((item, i) => <div key={item.label} data-index={i} className="flex flex-col items-start rounded-lg bg-card/40 p-3 sm:items-center sm:text-center">
                      <span className="font-default text-xs text-muted-foreground">{item.label}</span>
                      <span className="font-default text-sm font-semibold text-foreground">{item.value}</span>
                    </div>)}
                </div>
              </div>

            </div>
          </Card>
        </div>
      </div>
    </section>;
}